/**
 * Monaco-backed markdown editor for backlog item bodies, with a GitHub-style
 * "Write / Preview" toggle (the state tabs of ``SectionTabs``: the two views of one field).
 *
 * Deliberately much thinner than {@link JsonPayloadEditor}: markdown has no
 * schema to register, so none of that module's schema-registry bookkeeping
 * applies here. What is shared is the lazy import — Monaco is a large chunk
 * and only the backlog editor and the catalog editor ever need it.
 *
 * The editor stays mounted while the preview is showing, hidden with CSS
 * rather than unmounted. Unmounting would throw away Monaco's undo stack and
 * cursor position every time the operator glances at the rendered output,
 * which is exactly when they are mid-edit. The cost is one explicit
 * ``layout()`` call on the way back, because Monaco cannot measure itself
 * while its container is ``display: none``.
 */

import { Suspense, lazy, useEffect, useId, useRef, useState } from "react";
import type { OnMount } from "@monaco-editor/react";

import { MarkdownView } from "@/components/MarkdownView";
import { Spinner } from "@/components/Spinner";
import { SectionTabs, TabPanel, type StateTab } from "@/components/ui";

const MonacoEditor = lazy(() =>
  import("@monaco-editor/react").then((m) => ({ default: m.default })),
);

type Mode = "write" | "preview";

const TABS: readonly StateTab<Mode>[] = [
  { value: "write", label: "Write" },
  { value: "preview", label: "Preview" },
];

export interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Editor height in px. Defaults to 480. */
  height?: number;
}

export function MarkdownEditor({
  value,
  onChange,
  height = 480,
}: MarkdownEditorProps) {
  const [mode, setMode] = useState<Mode>("write");
  // Ties each tab to its panel; unique per editor (a page may hold several).
  const tabsId = useId();
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

  useEffect(() => {
    if (mode === "write") editorRef.current?.layout();
  }, [mode]);

  return (
    <div className="overflow-hidden rounded border border-line-field">
      <div className="bg-sunken px-2">
        <SectionTabs label="Editor mode" idPrefix={tabsId} panels="all" tabs={TABS} value={mode} onChange={setMode} />
      </div>

      {/* Both panes are laid out at the same height so switching tabs does
          not make the page jump under the operator's cursor. */}
      <TabPanel idPrefix={tabsId} value="write" active={mode === "write"}>
        <Suspense fallback={<Spinner label="Loading editor…" />}>
          <MonacoEditor
            height={height}
            language="markdown"
            value={value}
            onChange={(next) => onChange(next ?? "")}
            onMount={(editor) => {
              editorRef.current = editor;
            }}
            options={{
              minimap: { enabled: false },
              wordWrap: "on",
              lineNumbers: "off",
              fontSize: 13,
              scrollBeyondLastLine: false,
              renderWhitespace: "none",
            }}
          />
        </Suspense>
      </TabPanel>

      <TabPanel idPrefix={tabsId} value="preview" active={mode === "preview"}>
        {mode === "preview" && (
          <div className="overflow-y-auto p-4" style={{ height }}>
            <MarkdownView value={value} />
          </div>
        )}
      </TabPanel>
    </div>
  );
}
